import { type CountryCode, type ProjectCode } from '@constants'
import { useDeepCompareEffect } from '@dni-hooks/use-deep-compare-effect'
import { useDebounceValue } from '@dni-hooks/useDebounceValue'
import { type Nullable } from '@hooks-api/types/nullable'
import { create } from 'zustand'
import { type CartItem } from '../../types'
import { useCampaignProductsInfo } from '../useCampaignProductsInfo'
import { cartStoreMap, type PlatformType, Platform } from './store-map'

type ExtraProductIdsSetter = (prev: number[]) => number[]

const useFpB2bExtraProductIds = create<{
  extraProductIds: number[]
  setExtraProductIds: (setter: ExtraProductIdsSetter) => void
}>()(set => ({
  extraProductIds: [],
  setExtraProductIds: (setter: ExtraProductIdsSetter) => {
    set(({ extraProductIds }) => ({ extraProductIds: setter(extraProductIds) }))
  },
}))

export interface UseFpB2bProductsInfoProps {
  projectCode: ProjectCode
  countryCode: CountryCode
  campaignId: number
  campaignCode: Nullable<string>
  extraProductIds?: CartItem['cartItemId'][]
  platform: PlatformType
}

export const useFpB2bProductsInfo = (props: UseFpB2bProductsInfoProps) => {
  const { projectCode, countryCode, campaignCode, campaignId, extraProductIds: extraProductIdsProp = [], platform } = props
  const { useCartStore } = cartStoreMap[platform]
  const [cartValues] = useCartStore(campaignId)
  const { extraProductIds, setExtraProductIds } = useFpB2bExtraProductIds()
  const debouncedExtraProductIds = useDebounceValue(extraProductIds, 200)

  useDeepCompareEffect(() => {
    /**
     * Merchant 的 AI 推薦 cartItemId 不是 variation_id，需用 variation_id 來取得 product。
     * STM 購物車的 cartItemId 就是 variation_id，只收集外部傳入的 extraProductIds（贈品、AI 下單推薦品）。
     */
    const variationIds = platform === Platform.MERCHANT
      ? cartValues.cart.items.map(item => Number(item.variation_id)).filter(variationId => !Number.isNaN(variationId))
      : []
    const productIds = extraProductIdsProp.map(productId => Number(productId)).filter(productId => !Number.isNaN(productId))
    setExtraProductIds(prev => [...new Set([...prev, ...variationIds, ...productIds])])
  }, [cartValues.cart.items, extraProductIdsProp])

  /**
   * Merchant: 過濾掉 butler 有 orderItemId 的 cartItem（AI 推薦品用 extraProductIds 取得）
   * STM: 不過濾
   */
  const cartItemsForQuery = platform === Platform.MERCHANT
    ? cartValues.cart.items.filter(item => !item.butler.orderItemId)
    : cartValues.cart.items

  const { productMap, revalidateProductsInfo, getProducts } = useCampaignProductsInfo({
    cartItems: cartItemsForQuery,
    projectCode,
    countryCode,
    campaignCode,
    extraProductIds: debouncedExtraProductIds,
    merchantId: cartValues.merchant.index,
  })

  return {
    productMap,
    revalidateProductsInfo,
    getProducts,
  }
}
